import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable, of, ReplaySubject, tap, finalize, catchError, EMPTY, Subscription, shareReplay } from 'rxjs';
import { clientSettings } from 'go-modules/models/common/client.settings';
import { Task, TaskType, TaskRequirements, TaskOperators } from '@go-modules/interfaces/task';
import { NgxFeatureFlagService } from '../feature-flag/feature-flag.service';
import { SelectedService, selectedServiceToken } from 'go-modules/services/selected/selected.service';
import { Inject } from '@angular/core';
import { Activity } from '@go-modules/interfaces/activity';
import { EventService } from '../event/event.service';
import { EVENT_NAMES } from '../event/event-names.constants';
import { ActivityEditModeService } from '../activity-edit-mode/activity-edit-mode.service';
import { activityTemplateModelToken } from 'go-modules/models/activity-template/activity-template.factory';
import { ActivityTemplateFactory } from 'go-modules/models/activity-template/activity-template.schema';

interface TaskCacheEntry {
	/** The subject that subscribers receive. Never ending stream */
	subject$: ReplaySubject<Task[]>;

	/** Current cached tasks for synchronous access */
	currentTasks?: Task[];

	/** undefined ⇒ no data fetched yet, true ⇒ data fetched (even if empty array) */
	fetched?: boolean;

	/** Timestamp when data was fetched for TTL cleanup */
	fetchedAt?: number;

	/** Internal only. Track in-flight requests to prevent duplicate API calls when waiting for the first response */
	inFlight$?: Observable<Task[]>;

	/** Internal only. Track subscription to in-flight request for cancellation */
	inFlightSubscription?: Subscription;
}

@Injectable({
	providedIn: 'root'
})
export class NgxTaskService {
	private cache = new Map<number, TaskCacheEntry>();
	private readonly CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

	constructor (
		private http: HttpClient,
		private featureFlag: NgxFeatureFlagService,
		@Inject(selectedServiceToken) private selectedService: SelectedService,
		private eventService: EventService,
		private activityEditModeService: ActivityEditModeService,
		@Inject(activityTemplateModelToken) private ActivityTemplateModel: ActivityTemplateFactory
	) {
		// Clean up expired entries every 5 minutes
		setInterval(() => this.cleanupExpiredEntries(), this.CACHE_TTL_MS);

		// Listen for task completion changes from PubNub
		this.eventService.listen(EVENT_NAMES.TASK_USER_COMPLETION_CHANGED).subscribe((event) => {
			const activity = this.selectedService.getActivity();
			if (activity && event.data?.task_id !== undefined) {
				this.updateTaskCompletion(activity.activity_id, event.data.task_id, event.data.completed);
			}
		});
	}

	public getTasks (activityId: number): Observable<Task[]> {
		const entry = this.getOrCreateEntry(activityId);

		// If we haven't fetched data yet and don't have an in-flight request, start one
		if (entry.fetched === undefined && !entry.inFlight$) {
			this.ensureFetch(entry, activityId);
		}

		return entry.subject$.asObservable();
	}

	public hasSubmitTask (activityId: number): boolean {
		if (!this.shouldShowTasks()) {
			return false;
		}

		const entry = this.cache.get(activityId);
		if (!entry || !entry.fetched || !entry.currentTasks) {
			return false;
		}

		return entry.currentTasks.some((task) => task.type === TaskType.Submit);
	}

	public allTasksComplete (activityId: number): Observable<boolean> {
		return this.getTasks(activityId).pipe(
			map((tasks) => tasks.length > 0 && tasks.every((task) => task.completed)),
			shareReplay(1)
		);
	}

	// Warning: this method should only be used if we know the activity is loaded
	public shouldShowTasks (): boolean {
		const activity = this.selectedService.getActivity();
		return this.featureFlag.isAvailable('ACTIVITY_TASKS') && activity?.show_tasks === true;
	}

	public shouldShowTasksObservable (): Observable<boolean> {
		return this.selectedService.selectedSubject.asObservable().pipe(
			map((selected) => selected.activity),
			map((activity) => this.featureFlag.isAvailable('ACTIVITY_TASKS') && activity?.show_tasks === true)
		);
	}

	public getDefaultTaskTypeForUser (): Observable<TaskType> {
		const group = this.selectedService.getGroup();
		if (group?.hasReviewerRole(true)) {
			return of(TaskType.Evaluate);
		}
		return this.getFirstIncompleteTaskType();
	}

	public isValidTaskType (type: string): boolean {
		return Object.values(TaskType).includes(type as TaskType);
	}

	public canAccessTaskType (type: TaskType): Observable<boolean> {
		// Evaluate is reviewer+ only
		if (type === TaskType.Evaluate) {
			const group = this.selectedService.getGroup();
			const hasReviewerRole = group?.hasReviewerRole(true);
			if (hasReviewerRole) {
				return of(true);
			} else {
				return of(false);
			}
		}

		const activity = this.selectedService.getActivity();
		if (!activity) {
			return of(false);
		}

		if (this.activityEditModeService.isEditMode) {
			return of(this.activityEditModeService.getEditedTasks().some((task) => task.type === type));
		}

		return this.getTasks(activity.activity_id).pipe(
			map((tasks) => tasks.some((task) => task.type === type))
		);
	}

	private getFirstIncompleteTaskType (): Observable<TaskType> {
		return this.getTasks(this.selectedService.getActivity().activity_id).pipe(
			map((tasks) => {
				const sortedTasks = tasks
					.sort((a, b) => a.sort_order - b.sort_order);
				const firstIncompleteTask = sortedTasks
					.find((task) => !task.completed);
				return firstIncompleteTask?.type || sortedTasks.at(-1)?.type;
			})
		);
	}

	private getOrCreateEntry (activityId: number): TaskCacheEntry {
		const existingEntry = this.cache.get(activityId);

		// Check if entry exists and is not expired
		if (existingEntry && existingEntry.fetched && existingEntry.fetchedAt) {
			const isExpired = Date.now() - existingEntry.fetchedAt > this.CACHE_TTL_MS;
			if (isExpired) {
				this.clearCache(activityId);
				return this.createEntry(activityId);
			}
			return existingEntry;
		}

		return existingEntry ?? this.createEntry(activityId);
	}

	private createEntry (activityId: number): TaskCacheEntry {
		const entry: TaskCacheEntry = { subject$: new ReplaySubject<Task[]>(1) };
		this.cache.set(activityId, entry);
		return entry;
	}

	/**
	 * Cancel an in-flight request
	 */
	private cancelInFlightRequest (entry: TaskCacheEntry): void {
		if (entry.inFlightSubscription) {
			entry.inFlightSubscription.unsubscribe();
			entry.inFlightSubscription = undefined;
		}
		entry.inFlight$ = undefined;
	}

	/**
	 * Start the HTTP call if not already running
	 */
	private ensureFetch (entry: TaskCacheEntry, activityId: number): void {
		if (entry.inFlight$) return;

		entry.inFlight$ = this.http.get<Task[]>(`${clientSettings.GoReactV2API}/activities/${activityId}/tasks`).pipe(
			tap((tasks) => {
				entry.fetched = true;
				entry.fetchedAt = Date.now();
				entry.currentTasks = tasks;
				entry.subject$.next(tasks);
			}),
			catchError((error) => {
				// clear on error so future requests can retry
				this.cache.delete(activityId);
				// propagate error to all subscribers via the subject
				entry.subject$.error(error);
				// complete the observable chain
				return EMPTY;
			}),
			finalize(() => {
				delete entry.inFlight$;
				delete entry.inFlightSubscription;
			})
		);

		// Fire immediately so the result is cached even if no component subscribed to subject$ yet
		entry.inFlightSubscription = entry.inFlight$.subscribe();
	}

	// Update tasks in cache without clearing
	public updateTasks (activityId: number, tasks: Task[]): void {
		const entry = this.cache.get(activityId);
		if (entry) {
			entry.currentTasks = tasks;
			entry.subject$.next(tasks);
			entry.fetched = true;
			entry.fetchedAt = Date.now();
		} else {
			// If no entry exists, create one with the new tasks
			const newEntry = this.createEntry(activityId);
			newEntry.currentTasks = tasks;
			newEntry.subject$.next(tasks);
			newEntry.fetched = true;
			newEntry.fetchedAt = Date.now();
		}
	}

	public updateTaskCompletion (activityId: number, taskId: number, completed: boolean): void {
		const entry = this.cache.get(activityId);
		if (entry && entry.fetched) {
			let currentTasks: Task[] = [];
			const subscription = entry.subject$.subscribe((tasks) => {
				currentTasks = tasks;
			});
			subscription.unsubscribe();

			const updatedTasks = currentTasks.map((task) =>
				task.id === taskId
					? { ...task, completed }
					: task
			);
			entry.subject$.next(updatedTasks);
		}
	}

	/**
	 * Get the default sort order for a task type
	 */
	public getTaskTypeDefaultOrder (taskType: TaskType): number {
		switch (taskType) {
			case TaskType.Present:
				return 1;
			case TaskType.Reflect:
				return 2;
			case TaskType.Submit:
				return 3;
			case TaskType.React:
				return 4;
			case TaskType.Review:
				return 5;
			case TaskType.Evaluate:
				return 6;
			default:
				return 999;
		}
	}

	/**
	 * Get the default task requirements for a task type
	 */
	public getTaskTypeDefaultRequirement (taskType: TaskType):
		Partial<Record<TaskRequirements, number>> |
		Partial<Record<TaskOperators, Partial<Record<TaskRequirements, number>>[]>>
	{
		switch (taskType) {
			case TaskType.Present:
				return { [TaskRequirements.PresentedSessions]: 1 };
			case TaskType.Reflect:
				return { [TaskRequirements.ReflectedComments]: 1 };
			case TaskType.Submit:
				return { [TaskRequirements.SubmittedSessions]: 1 };
			case TaskType.React:
				return { [TaskRequirements.ReactedComments]: 1 };
			case TaskType.Review:
				return {
					[TaskOperators.Or]: [
						{ [TaskRequirements.ReviewedInstructorComments]: 1 },
						{ [TaskRequirements.ReviewedInstructorRubrics]: 1 }
					]
				};
			default:
				return {};
		}
	}

	/**
	 * Check if a task type is compatible with an activity based on activity settings
	 */
	public isTaskTypeCompatibleWith (taskType: TaskType, activity: Activity): boolean {
		const isConversationBoard = activity.is_conversation && !activity.has_response_media;

		switch (taskType) {
			case TaskType.Reflect:
				return activity.has_response_media;
			case TaskType.Submit:
				return activity.has_response_media &&
					!activity.live_session_enabled &&
					!activity.has_single_recording_attempt;
			case TaskType.React:
				// React is always hidden for comment-only and conversation board regardless of peer_review setting,
				// since there are no peer videos to review
				const template = this.ActivityTemplateModel.model(activity.template);
				if (!template.isWrite('peer_review')) {
					return false;
				}
				// React can always be added regardless of peer-review (backend handles updating peer_review)
				return true;
			case TaskType.Review:
				return !isConversationBoard;
			default:
				return true;
		}
	}

	// Clear single entry or entire cache
	public clearCache (activityId?: number): void {
		if (activityId) {
			const entry = this.cache.get(activityId);
			if (entry) {
				this.cancelInFlightRequest(entry);
				entry.subject$.complete();
				this.cache.delete(activityId);
			}
		} else {
			this.cache.forEach((entry) => {
				this.cancelInFlightRequest(entry);
				entry.subject$.complete();
			});
			this.cache.clear();
		}
	}

	/**
	 * Generate default tasks dynamically based on activity compatibility
	 */
	public getDefaultTasks (activity: Activity): Task[] {
		const allTaskTypes = Object.values(TaskType);

		return allTaskTypes
			.filter((taskType) => {
				// Evaluate is not a real task
				return this.isTaskTypeCompatibleWith(taskType, activity)
					&& taskType !== TaskType.Evaluate;
			})
			.map((taskType) => ({
				id: 0,
				completed: false,
				name: '',
				requirements: this.getTaskTypeDefaultRequirement(taskType),
				sort_order: this.getTaskTypeDefaultOrder(taskType),
				type: taskType
			}))
			.sort((a, b) => a.sort_order - b.sort_order);
	}

	/**
	 * Clean up expired cache entries to prevent memory leaks
	 */
	private cleanupExpiredEntries (): void {
		const now = Date.now();
		const expiredKeys: number[] = [];

		this.cache.forEach((entry, activityId) => {
			if (entry.fetched && entry.fetchedAt && (now - entry.fetchedAt > this.CACHE_TTL_MS)) {
				expiredKeys.push(activityId);
			}
		});

		expiredKeys.forEach((activityId) => this.clearCache(activityId));
	}
}
